import React, { useState, useRef, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { 
  Video, VideoOff, Mic, MicOff, Phone, PhoneOff, Monitor, 
  MessageSquare, Users, Settings, Maximize, Minimize, 
  FileText, Clock, Shield, Brain, Camera, Volume2,
  ChevronRight, Plus, X, Share2, Grid, Layout, PenTool,
  Download, Upload, Clipboard, Calendar, Bell, Check,
  Wifi, WifiOff, RefreshCw, Pause, Play, MoreVertical,
  Image, Smile, Paperclip, Eye, EyeOff, Lock, AlertTriangle,
  Eraser, Circle, Square, Type, Undo, Redo, Trash2, Save
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// HIPAA Compliance Modal Component
const HIPAAComplianceModal = ({ isOpen, onAccept, onDecline, actionType }) => {
  const [acknowledged, setAcknowledged] = useState(false);
  
  // Reset acknowledged state when modal opens
  useEffect(() => {
    if (isOpen) {
      setAcknowledged(false);
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">HIPAA Compliance Notice</h2>
              <p className="text-red-100 text-sm">Protected Health Information (PHI) Warning</p>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-red-800 mb-2">Action: {actionType}</h3>
                <p className="text-red-700 text-sm">
                  You are about to access or transmit Protected Health Information (PHI). 
                  This action is logged and audited in compliance with HIPAA regulations.
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-4 mb-6">
            <h4 className="font-semibold text-gray-900">By proceeding, you acknowledge that:</h4>
            <ul className="space-y-3 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>You are authorized to access this patient's Protected Health Information</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>This session is being conducted for legitimate healthcare purposes</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>All communications are encrypted and HIPAA compliant</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>You will not share PHI with unauthorized individuals</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Your actions are being logged for audit purposes</span>
              </li>
            </ul>
          </div>
          
          <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors" data-testid="hipaa-acknowledge-label">
            <input 
              type="checkbox" 
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
              data-testid="hipaa-acknowledge-checkbox"
            />
            <span className="text-gray-700 font-medium">
              I acknowledge and agree to comply with HIPAA regulations
            </span>
          </label>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
          <button 
            onClick={onDecline}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="hipaa-cancel-btn"
          >
            Cancel
          </button>
          <button 
            onClick={onAccept}
            disabled={!acknowledged}
            className={`px-6 py-2 rounded-lg transition-colors flex items-center gap-2 ${
              acknowledged 
                ? 'bg-red-600 text-white hover:bg-red-700' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            data-testid="hipaa-accept-btn"
          >
            <Shield className="w-4 h-4" />
            I Accept & Proceed
          </button>
        </div>
      </div>
    </div>
  );
};

// Whiteboard Component with Real-time Sync
const Whiteboard = ({ isOpen, onClose, sessionId, onSave, websocket }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#000000');
  const [lineWidth, setLineWidth] = useState(3);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [syncStatus, setSyncStatus] = useState('connected');
  const lastDrawPoint = useRef(null);
  
  useEffect(() => {
    if (isOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      saveState();
    }
  }, [isOpen]);
  
  // Listen for whiteboard sync messages from WebSocket
  useEffect(() => {
    if (!websocket) return;
    
    const handleMessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        
        if (message.type === 'whiteboard_sync' && canvasRef.current) {
          // Full canvas sync from server
          const img = new Image();
          img.src = message.canvas_data;
          img.onload = () => {
            const ctx = canvasRef.current.getContext('2d');
            ctx.drawImage(img, 0, 0);
          };
          setSyncStatus('synced');
        }
        
        if (message.type === 'whiteboard_update' && canvasRef.current) {
          // Canvas update from peer
          const img = new Image();
          img.src = message.canvas_data;
          img.onload = () => {
            const ctx = canvasRef.current.getContext('2d');
            ctx.drawImage(img, 0, 0);
          };
        }
        
        if (message.type === 'whiteboard_draw' && canvasRef.current) {
          // Real-time drawing from peer
          const ctx = canvasRef.current.getContext('2d');
          const { x, y, prevX, prevY, color: drawColor, lineWidth: drawLineWidth, tool: drawTool } = message.draw_data;
          
          ctx.beginPath();
          ctx.lineWidth = drawTool === 'eraser' ? drawLineWidth * 3 : drawLineWidth;
          ctx.lineCap = 'round';
          ctx.strokeStyle = drawTool === 'eraser' ? '#ffffff' : drawColor;
          ctx.moveTo(prevX, prevY);
          ctx.lineTo(x, y);
          ctx.stroke();
        }
        
        if (message.type === 'whiteboard_clear' && canvasRef.current) {
          // Clear canvas from peer
          const ctx = canvasRef.current.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
      } catch (e) {
        console.error('Whiteboard message error:', e);
      }
    };
    
    websocket.addEventListener('message', handleMessage);
    return () => websocket.removeEventListener('message', handleMessage);
  }, [websocket]);
  
  const saveState = () => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(canvas.toDataURL());
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  };
  
  // Send drawing data through WebSocket for real-time sync
  const sendDrawData = (x, y, prevX, prevY) => {
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      websocket.send(JSON.stringify({
        type: 'whiteboard_draw',
        draw_data: {
          x, y, prevX, prevY,
          color, lineWidth, tool
        }
      }));
    }
  };
  
  // Send full canvas update
  const sendCanvasUpdate = () => {
    if (websocket && websocket.readyState === WebSocket.OPEN && canvasRef.current) {
      websocket.send(JSON.stringify({
        type: 'whiteboard_update',
        canvas_data: canvasRef.current.toDataURL()
      }));
    }
  };
  
  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      const img = new Image();
      img.src = history[historyIndex - 1];
      img.onload = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.drawImage(img, 0, 0);
        sendCanvasUpdate();
      };
    }
  };
  
  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      const img = new Image();
      img.src = history[historyIndex + 1];
      img.onload = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.drawImage(img, 0, 0);
        sendCanvasUpdate();
      };
    }
  };
  
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    saveState();
    
    // Notify peers
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      websocket.send(JSON.stringify({ type: 'whiteboard_clear' }));
    }
  };
  
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setIsDrawing(true);
    lastDrawPoint.current = { x, y };
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  
  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    ctx.lineWidth = tool === 'eraser' ? lineWidth * 3 : lineWidth;
    ctx.lineCap = 'round';
    ctx.strokeStyle = tool === 'eraser' ? '#ffffff' : color;
    
    ctx.lineTo(x, y);
    ctx.stroke();
    
    // Send real-time drawing data to peers
    if (lastDrawPoint.current) {
      sendDrawData(x, y, lastDrawPoint.current.x, lastDrawPoint.current.y);
    }
    lastDrawPoint.current = { x, y };
  };
  
  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      lastDrawPoint.current = null;
      saveState();
      // Send final canvas state
      sendCanvasUpdate();
    }
  };
  
  const handleSave = async () => {
    if (canvasRef.current && onSave) {
      const canvasData = canvasRef.current.toDataURL();
      await onSave(canvasData);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-[90%] h-[85%] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gray-100 px-4 py-3 flex items-center justify-between border-b">
          <div className="flex items-center gap-4">
            <h3 className="font-bold text-gray-800 flex items-center gap-2">
              <PenTool className="w-5 h-5 text-purple-600" />
              Shared Whiteboard
              {websocket && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  syncStatus === 'connected' ? 'bg-green-100 text-green-600' :
                  syncStatus === 'synced' ? 'bg-blue-100 text-blue-600' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {syncStatus === 'connected' ? 'Live' : syncStatus === 'synced' ? 'Synced' : 'Offline'}
                </span>
              )}
            </h3>
            
            {/* Tools */}
            <div className="flex items-center gap-1 bg-white rounded-lg p-1 shadow-sm">
              <button 
                onClick={() => setTool('pen')}
                className={`p-2 rounded ${tool === 'pen' ? 'bg-purple-100 text-purple-600' : 'hover:bg-gray-100'}`}
                title="Pen"
              >
                <PenTool className="w-4 h-4" />
              </button>
              <button 
                onClick={() => setTool('eraser')}
                className={`p-2 rounded ${tool === 'eraser' ? 'bg-purple-100 text-purple-600' : 'hover:bg-gray-100'}`}
                title="Eraser"
              >
                <Eraser className="w-4 h-4" />
              </button>
            </div>
            
            {/* Color Picker */}
            <input 
              type="color" 
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-8 h-8 rounded cursor-pointer"
            />
            
            {/* Line Width */}
            <select 
              value={lineWidth}
              onChange={(e) => setLineWidth(Number(e.target.value))}
              className="px-2 py-1 border rounded text-sm"
            >
              <option value={1}>Thin</option>
              <option value={3}>Medium</option>
              <option value={6}>Thick</option>
              <option value={10}>Extra Thick</option>
            </select>
            
            {/* Undo/Redo */}
            <div className="flex items-center gap-1">
              <button onClick={undo} className="p-2 hover:bg-gray-100 rounded" title="Undo">
                <Undo className="w-4 h-4" />
              </button>
              <button onClick={redo} className="p-2 hover:bg-gray-100 rounded" title="Redo">
                <Redo className="w-4 h-4" />
              </button>
            </div>
            
            {/* Clear */}
            <button 
              onClick={clearCanvas}
              className="p-2 hover:bg-red-100 text-red-600 rounded"
              title="Clear All"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex items-center gap-2">
            <button 
              onClick={handleSave}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 text-sm"
            >
              <Save className="w-4 h-4" />
              Save
            </button>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Canvas */}
        <div className="flex-1 bg-gray-200 p-4 overflow-auto">
          <canvas
            ref={canvasRef}
            width={1200}
            height={800}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            className="bg-white rounded-lg shadow-lg cursor-crosshair mx-auto"
          />
        </div>
      </div>
    </div>
  );
};

// Main Telehealth Component
const BehavioralHealthTelehealth = () => {
  // State
  const [isCallActive, setIsCallActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [selectedClient, setSelectedClient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showWhiteboard, setShowWhiteboard] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [sessionNotes, setSessionNotes] = useState('');
  const [viewMode, setViewMode] = useState('speaker');
  const [connectionQuality, setConnectionQuality] = useState('excellent');
  const [isRecording, setIsRecording] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  
  // HIPAA Modal State
  const [showHIPAAModal, setShowHIPAAModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [hipaaActionType, setHipaaActionType] = useState('');
  
  // WebRTC State
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  
  // WebSocket Signaling State
  const websocketRef = useRef(null);
  const [signalingConnected, setSignalingConnected] = useState(false);
  const pendingIceCandidates = useRef([]);
  const isNegotiating = useRef(false);
  const makingOffer = useRef(false);

  // Get WebSocket URL
  const getWebSocketUrl = (roomId) => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const apiUrl = API_URL.replace(/^https?:\/\//, '');
    return `${wsProtocol}//${apiUrl}/ws/signaling/${roomId}`;
  };

  const clients = [
    { id: 1, name: 'Sarah Johnson', time: '9:00 AM', type: 'Individual Therapy', status: 'ready', waitingSince: '8:55 AM', email: 'sarah.j@email.com' },
    { id: 2, name: 'Michael Chen', time: '10:30 AM', type: 'Couples Counseling', status: 'waiting', waitingSince: '10:28 AM', email: 'mchen@email.com' },
    { id: 3, name: 'Emily Rodriguez', time: '1:00 PM', type: 'CBT Session', status: 'scheduled', email: 'emily.r@email.com' },
    { id: 4, name: 'David Thompson', time: '3:00 PM', type: 'Initial Assessment', status: 'scheduled', email: 'dthompson@email.com' },
  ];

  const waitingRoomClients = clients.filter(c => c.status === 'ready' || c.status === 'waiting');

  // Timer effect
  useEffect(() => {
    let interval;
    if (isCallActive) {
      interval = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isCallActive]);

  // Network change detection and reconnection
  useEffect(() => {
    const handleOnline = () => {
      console.log('Network online - attempting reconnection');
      if (isCallActive && !signalingConnected) {
        attemptReconnect();
      }
    };
    
    const handleOffline = () => {
      console.log('Network offline');
      setConnectionQuality('failed');
      setIsReconnecting(true);
    };
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isCallActive) {
        // User returned to tab - check connection
        if (websocketRef.current?.readyState !== WebSocket.OPEN) {
          attemptReconnect();
        }
      }
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isCallActive, signalingConnected]);

  // Attempt reconnection
  const attemptReconnect = async () => {
    if (reconnectAttempts >= 5) {
      console.log('Max reconnection attempts reached');
      setConnectionQuality('failed');
      return;
    }
    
    setIsReconnecting(true);
    setReconnectAttempts(prev => prev + 1);
    setConnectionQuality('connecting');
    
    try {
      // Close existing connections
      if (websocketRef.current) {
        websocketRef.current.close();
      }
      
      // Reconnect signaling
      if (currentSessionId) {
        await connectSignaling(currentSessionId);
        
        // Request renegotiation
        if (websocketRef.current?.readyState === WebSocket.OPEN) {
          websocketRef.current.send(JSON.stringify({
            type: 'reconnect'
          }));
        }
      }
      
      setIsReconnecting(false);
      setReconnectAttempts(0);
    } catch (error) {
      console.error('Reconnection failed:', error);
      // Retry after delay
      setTimeout(attemptReconnect, Math.min(1000 * Math.pow(2, reconnectAttempts), 30000));
    }
  };

  // Connect to signaling server
  const connectSignaling = (roomId) => {
    return new Promise((resolve, reject) => {
      const wsUrl = getWebSocketUrl(roomId);
      console.log('Connecting to signaling server:', wsUrl);
      
      const ws = new WebSocket(wsUrl);
      websocketRef.current = ws;
      
      ws.onopen = () => {
        console.log('Signaling WebSocket connected');
        setSignalingConnected(true);
        
        // Join the room
        ws.send(JSON.stringify({
          type: 'join',
          user_id: 'therapist_' + Date.now(),
          user_name: 'Therapist',
          user_role: 'host'
        }));
        
        resolve(ws);
      };
      
      ws.onmessage = async (event) => {
        const message = JSON.parse(event.data);
        await handleSignalingMessage(message);
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setSignalingConnected(false);
        reject(error);
      };
      
      ws.onclose = () => {
        console.log('Signaling WebSocket closed');
        setSignalingConnected(false);
        
        // Attempt reconnect if call is still active
        if (isCallActive && !isReconnecting) {
          setTimeout(() => attemptReconnect(), 2000);
        }
      };
    });
  };

  // Handle signaling messages
  const handleSignalingMessage = async (message) => {
    console.log('Signaling message:', message.type);
    
    switch (message.type) {
      case 'joined':
        console.log('Joined room:', message.room_id);
        // If there are other participants, create offer
        if (message.participants && message.participants.length > 0) {
          await createAndSendOffer();
        }
        break;
      
      case 'user_joined':
        console.log('User joined:', message.user);
        // New user joined - create offer for them
        if (peerConnectionRef.current) {
          await createAndSendOffer();
        }
        break;
      
      case 'user_left':
        console.log('User left:', message.user);
        break;
      
      case 'offer':
        console.log('Received offer from:', message.from_user?.user_name);
        await handleOffer(message.offer, message.from_id);
        break;
      
      case 'answer':
        console.log('Received answer from:', message.from_user?.user_name);
        await handleAnswer(message.answer);
        break;
      
      case 'ice-candidate':
        console.log('Received ICE candidate');
        await handleIceCandidate(message.candidate);
        break;
      
      case 'reconnect_ack':
        console.log('Reconnection acknowledged');
        if (message.should_renegotiate) {
          await createAndSendOffer();
        }
        break;
      
      case 'peer_reconnecting':
        console.log('Peer is reconnecting:', message.user);
        break;
      
      case 'renegotiate':
        console.log('Renegotiation requested');
        await createAndSendOffer();
        break;
      
      case 'ping':
        websocketRef.current?.send(JSON.stringify({ type: 'pong' }));
        break;
      
      default:
        console.log('Unknown message type:', message.type);
    }
  };

  // Create and send SDP offer
  const createAndSendOffer = async () => {
    if (!peerConnectionRef.current || makingOffer.current) return;
    
    try {
      makingOffer.current = true;
      
      const offer = await peerConnectionRef.current.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      });
      
      await peerConnectionRef.current.setLocalDescription(offer);
      
      websocketRef.current?.send(JSON.stringify({
        type: 'offer',
        offer: peerConnectionRef.current.localDescription
      }));
      
      console.log('Offer sent');
    } catch (error) {
      console.error('Error creating offer:', error);
    } finally {
      makingOffer.current = false;
    }
  };

  // Handle incoming SDP offer
  const handleOffer = async (offer, fromId) => {
    if (!peerConnectionRef.current) return;
    
    try {
      // Handle glare (both sides sending offers)
      const offerCollision = makingOffer.current || 
        peerConnectionRef.current.signalingState !== 'stable';
      
      if (offerCollision) {
        // We're the polite peer - rollback our offer
        await peerConnectionRef.current.setLocalDescription({ type: 'rollback' });
      }
      
      await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(offer));
      
      // Process any pending ICE candidates
      for (const candidate of pendingIceCandidates.current) {
        await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
      }
      pendingIceCandidates.current = [];
      
      const answer = await peerConnectionRef.current.createAnswer();
      await peerConnectionRef.current.setLocalDescription(answer);
      
      websocketRef.current?.send(JSON.stringify({
        type: 'answer',
        answer: peerConnectionRef.current.localDescription,
        target_id: fromId
      }));
      
      console.log('Answer sent');
    } catch (error) {
      console.error('Error handling offer:', error);
    }
  };

  // Handle incoming SDP answer
  const handleAnswer = async (answer) => {
    if (!peerConnectionRef.current) return;
    
    try {
      if (peerConnectionRef.current.signalingState === 'have-local-offer') {
        await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(answer));
        
        // Process any pending ICE candidates
        for (const candidate of pendingIceCandidates.current) {
          await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
        }
        pendingIceCandidates.current = [];
        
        console.log('Answer applied');
      }
    } catch (error) {
      console.error('Error handling answer:', error);
    }
  };

  // Handle incoming ICE candidate
  const handleIceCandidate = async (candidate) => {
    if (!candidate) return;
    
    try {
      if (peerConnectionRef.current?.remoteDescription) {
        await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
      } else {
        // Queue candidate until remote description is set
        pendingIceCandidates.current.push(candidate);
      }
    } catch (error) {
      console.error('Error adding ICE candidate:', error);
    }
  };

  // Initialize WebRTC
  const initializeWebRTC = async () => {
    try {
      // Get local media stream
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      setLocalStream(stream);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      // Create peer connection with FREE STUN and TURN servers
      // TURN servers help when users are behind strict firewalls/NATs
      const configuration = {
        iceServers: [
          // Google's free STUN servers
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' },
          { urls: 'stun:stun3.l.google.com:19302' },
          { urls: 'stun:stun4.l.google.com:19302' },
          
          // Open Relay Project - FREE TURN server (20GB/month)
          // Best for firewall traversal - supports ports 80/443
          {
            urls: 'turn:openrelay.metered.ca:80',
            username: 'openrelayproject',
            credential: 'openrelayproject'
          },
          {
            urls: 'turn:openrelay.metered.ca:443',
            username: 'openrelayproject',
            credential: 'openrelayproject'
          },
          {
            urls: 'turn:openrelay.metered.ca:443?transport=tcp',
            username: 'openrelayproject',
            credential: 'openrelayproject'
          },
          // TURNS (TLS encrypted) for maximum security
          {
            urls: 'turns:openrelay.metered.ca:443',
            username: 'openrelayproject',
            credential: 'openrelayproject'
          },
          
          // Cloudflare STUN (free)
          { urls: 'stun:stun.cloudflare.com:3478' },
          
          // FreeTURN.net - backup free TURN
          {
            urls: 'turn:freestun.net:3478',
            username: 'free',
            credential: 'free'
          }
        ],
        // Use all available candidates for best connectivity
        iceCandidatePoolSize: 10,
        // Prefer relay (TURN) for HIPAA compliance - more secure
        iceTransportPolicy: 'all'
      };
      
      const pc = new RTCPeerConnection(configuration);
      peerConnectionRef.current = pc;
      
      // Add local stream to peer connection
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });
      
      // Handle remote stream
      pc.ontrack = (event) => {
        setRemoteStream(event.streams[0]);
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = event.streams[0];
        }
      };
      
      // Handle ICE candidates to detect TURN usage and send through signaling
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          const candidateType = event.candidate.type;
          console.log('ICE Candidate type:', candidateType);
          
          // Send ICE candidate through signaling server
          if (websocketRef.current?.readyState === WebSocket.OPEN) {
            websocketRef.current.send(JSON.stringify({
              type: 'ice-candidate',
              candidate: event.candidate
            }));
          }
          
          // 'relay' means TURN server is being used
          if (candidateType === 'relay') {
            console.log('Using TURN relay for secure connection');
          }
        }
      };
      
      // Monitor ICE connection state
      pc.oniceconnectionstatechange = () => {
        const state = pc.iceConnectionState;
        console.log('ICE Connection state:', state);
        switch (state) {
          case 'checking':
            setConnectionQuality('connecting');
            break;
          case 'connected':
          case 'completed':
            setConnectionQuality('excellent');
            setIsReconnecting(false);
            setReconnectAttempts(0);
            break;
          case 'disconnected':
            setConnectionQuality('poor');
            // Attempt ICE restart
            if (!isReconnecting) {
              console.log('ICE disconnected - attempting restart');
              attemptIceRestart();
            }
            break;
          case 'failed':
            setConnectionQuality('failed');
            // Attempt full reconnection
            if (!isReconnecting) {
              attemptReconnect();
            }
            break;
          default:
            break;
        }
      };
      
      // Handle connection state changes
      pc.onconnectionstatechange = () => {
        console.log('Connection state:', pc.connectionState);
        if (pc.connectionState === 'connected') {
          setConnectionQuality('excellent');
          setIsReconnecting(false);
          // Get connection stats to check if using TURN
          pc.getStats().then(stats => {
            stats.forEach(report => {
              if (report.type === 'candidate-pair' && report.state === 'succeeded') {
                console.log('Active candidate pair:', report);
              }
            });
          });
        } else if (pc.connectionState === 'disconnected') {
          setConnectionQuality('poor');
        } else if (pc.connectionState === 'failed') {
          setConnectionQuality('failed');
          // Attempt reconnection
          if (!isReconnecting) {
            attemptReconnect();
          }
        }
      };
      
      // Handle negotiation needed (for renegotiation)
      pc.onnegotiationneeded = async () => {
        console.log('Negotiation needed');
        if (!isNegotiating.current) {
          isNegotiating.current = true;
          try {
            await createAndSendOffer();
          } finally {
            isNegotiating.current = false;
          }
        }
      };
      
      return true;
    } catch (error) {
      console.error('Error initializing WebRTC:', error);
      return false;
    }
  };

  // Attempt ICE restart for temporary disconnections
  const attemptIceRestart = async () => {
    if (!peerConnectionRef.current) return;
    
    console.log('Attempting ICE restart');
    setConnectionQuality('connecting');
    
    try {
      const offer = await peerConnectionRef.current.createOffer({ iceRestart: true });
      await peerConnectionRef.current.setLocalDescription(offer);
      
      websocketRef.current?.send(JSON.stringify({
        type: 'offer',
        offer: peerConnectionRef.current.localDescription
      }));
      
      console.log('ICE restart offer sent');
    } catch (error) {
      console.error('ICE restart failed:', error);
      // Fall back to full reconnection
      attemptReconnect();
    }
  };

  // Cleanup WebRTC and signaling
  const cleanupWebRTC = () => {
    // Close WebSocket connection
    if (websocketRef.current) {
      websocketRef.current.send(JSON.stringify({ type: 'leave' }));
      websocketRef.current.close();
      websocketRef.current = null;
    }
    setSignalingConnected(false);
    
    // Stop local stream
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
    }
    
    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    setRemoteStream(null);
    
    // Reset reconnection state
    setIsReconnecting(false);
    setReconnectAttempts(0);
    pendingIceCandidates.current = [];
  };

  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // HIPAA-protected action handler
  const handleProtectedAction = (action, actionType, callback) => {
    setPendingAction(() => callback);
    setHipaaActionType(actionType);
    setShowHIPAAModal(true);
  };

  const handleHIPAAAccept = async () => {
    setShowHIPAAModal(false);
    
    // Log HIPAA consent to backend
    if (currentSessionId) {
      try {
        await fetch(`${API_URL}/api/telehealth/sessions/${currentSessionId}/hipaa-consent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            session_id: currentSessionId,
            acknowledged: true
          })
        });
      } catch (error) {
        console.error('Error logging HIPAA consent:', error);
      }
    }
    
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const handleHIPAADecline = () => {
    setShowHIPAAModal(false);
    setPendingAction(null);
  };

  // Start session with HIPAA check
  const startSession = async (client) => {
    handleProtectedAction(
      'start_session',
      'Start Telehealth Session',
      async () => {
        let roomId = null;
        
        // Create session in backend
        try {
          const response = await fetch(`${API_URL}/api/telehealth/sessions`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              client_id: client.id.toString(),
              client_name: client.name,
              session_type: client.type
            })
          });
          
          const data = await response.json();
          if (data.success) {
            roomId = data.room_id;
            setCurrentSessionId(roomId);
            
            // Start session in backend
            await fetch(`${API_URL}/api/telehealth/sessions/${roomId}/start`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });
          }
        } catch (error) {
          console.error('Error creating session:', error);
        }
        
        // Initialize WebRTC (gets local media and creates peer connection)
        await initializeWebRTC();
        
        // Connect to signaling server for peer-to-peer communication
        if (roomId) {
          try {
            await connectSignaling(roomId);
            console.log('Connected to signaling server for room:', roomId);
          } catch (error) {
            console.error('Error connecting to signaling server:', error);
          }
        }
        
        setSelectedClient(client);
        setIsCallActive(true);
        setCallDuration(0);
      }
    );
  };

  // End session
  const endSession = async () => {
    // End session in backend
    if (currentSessionId) {
      try {
        await fetch(`${API_URL}/api/telehealth/sessions/${currentSessionId}/end`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ notes: sessionNotes })
        });
      } catch (error) {
        console.error('Error ending session:', error);
      }
    }
    
    cleanupWebRTC();
    setIsCallActive(false);
    setSelectedClient(null);
    setCallDuration(0);
    setShowChat(false);
    setShowNotes(false);
    setShowWhiteboard(false);
    setCurrentSessionId(null);
    setSessionNotes('');
  };

  // Toggle mute with HIPAA check
  const toggleMute = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach(track => {
        track.enabled = isMuted;
      });
    }
    setIsMuted(!isMuted);
  };

  // Toggle video with HIPAA check
  const toggleVideo = () => {
    if (localStream) {
      localStream.getVideoTracks().forEach(track => {
        track.enabled = !isVideoOn;
      });
    }
    setIsVideoOn(!isVideoOn);
  };

  // Screen share with HIPAA check
  const toggleScreenShare = () => {
    handleProtectedAction(
      'screen_share',
      'Share Screen (PHI may be visible)',
      async () => {
        if (!isScreenSharing) {
          try {
            const screenStream = await navigator.mediaDevices.getDisplayMedia({
              video: true
            });
            // Replace video track with screen share
            if (peerConnectionRef.current && localStream) {
              const videoTrack = screenStream.getVideoTracks()[0];
              const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video');
              if (sender) {
                sender.replaceTrack(videoTrack);
              }
              
              videoTrack.onended = () => {
                setIsScreenSharing(false);
                // Restore camera
                const cameraTrack = localStream.getVideoTracks()[0];
                if (sender && cameraTrack) {
                  sender.replaceTrack(cameraTrack);
                }
              };
            }
            setIsScreenSharing(true);
          } catch (error) {
            console.error('Error sharing screen:', error);
          }
        } else {
          setIsScreenSharing(false);
        }
      }
    );
  };

  // Recording with HIPAA check
  const toggleRecording = () => {
    handleProtectedAction(
      'recording',
      isRecording ? 'Stop Session Recording' : 'Start Session Recording (PHI will be recorded)',
      () => {
        setIsRecording(!isRecording);
      }
    );
  };

  // Whiteboard with HIPAA check
  const openWhiteboard = () => {
    handleProtectedAction(
      'whiteboard',
      'Open Shared Whiteboard (Client can see)',
      () => {
        setShowWhiteboard(true);
      }
    );
  };

  // Save whiteboard
  const saveWhiteboard = async (canvasData) => {
    if (currentSessionId) {
      try {
        await fetch(`${API_URL}/api/telehealth/sessions/${currentSessionId}/whiteboard`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            session_id: currentSessionId,
            canvas_data: canvasData
          })
        });
      } catch (error) {
        console.error('Error saving whiteboard:', error);
      }
    }
  };

  // Send chat message
  const sendMessage = async () => {
    if (newMessage.trim()) {
      const msg = {
        sender: 'therapist',
        text: newMessage,
        time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
      };
      
      setMessages([...messages, msg]);
      
      // Save to backend
      if (currentSessionId) {
        try {
          await fetch(`${API_URL}/api/telehealth/sessions/${currentSessionId}/chat`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              session_id: currentSessionId,
              sender: 'therapist',
              message: newMessage
            })
          });
        } catch (error) {
          console.error('Error sending message:', error);
        }
      }
      
      setNewMessage('');
    }
  };

  // Save session notes
  const saveNotes = async () => {
    if (currentSessionId && sessionNotes) {
      try {
        await fetch(`${API_URL}/api/telehealth/sessions/${currentSessionId}/notes`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(sessionNotes)
        });
      } catch (error) {
        console.error('Error saving notes:', error);
      }
    }
  };

  return (
    <DashboardLayout>
      {/* HIPAA Compliance Modal */}
      <HIPAAComplianceModal 
        isOpen={showHIPAAModal}
        onAccept={handleHIPAAAccept}
        onDecline={handleHIPAADecline}
        actionType={hipaaActionType}
      />
      
      {/* Whiteboard */}
      <Whiteboard 
        isOpen={showWhiteboard}
        onClose={() => setShowWhiteboard(false)}
        sessionId={currentSessionId}
        onSave={saveWhiteboard}
        websocket={websocketRef.current}
      />
      
      <div className="h-[calc(100vh-64px)] bg-gray-900 flex">
        {/* Main Video Area */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="bg-gray-800 px-6 py-3 flex items-center justify-between border-b border-gray-700">
            <div className="flex items-center gap-4">
              <Brain className="w-6 h-6 text-orange-500" />
              <div>
                <h1 className="text-white font-bold">Telehealth Session</h1>
                <div className="flex items-center gap-2 text-sm">
                  <span className="flex items-center gap-1 text-green-400">
                    <Shield className="w-3 h-3" />
                    HIPAA Compliant
                  </span>
                  <span className="text-gray-500">•</span>
                  <span className="flex items-center gap-1 text-green-400">
                    <Lock className="w-3 h-3" />
                    End-to-End Encrypted
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
                connectionQuality === 'excellent' ? 'bg-green-900/50' : 
                connectionQuality === 'good' ? 'bg-yellow-900/50' : 
                connectionQuality === 'connecting' ? 'bg-blue-900/50' :
                connectionQuality === 'failed' ? 'bg-red-900/50' :
                'bg-gray-700'
              }`}>
                {connectionQuality === 'connecting' ? (
                  <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />
                ) : connectionQuality === 'failed' ? (
                  <WifiOff className="w-4 h-4 text-red-400" />
                ) : (
                  <Wifi className={`w-4 h-4 ${
                    connectionQuality === 'excellent' ? 'text-green-400' : 
                    connectionQuality === 'good' ? 'text-yellow-400' : 
                    'text-red-400'
                  }`} />
                )}
                <span className={`text-xs capitalize ${
                  connectionQuality === 'excellent' ? 'text-green-400' : 
                  connectionQuality === 'good' ? 'text-yellow-400' : 
                  connectionQuality === 'connecting' ? 'text-blue-400' :
                  connectionQuality === 'failed' ? 'text-red-400' :
                  'text-gray-300'
                }`}>
                  {connectionQuality}
                </span>
              </div>
              
              {/* Reconnecting indicator */}
              {isReconnecting && (
                <div className="flex items-center gap-2 px-3 py-1 bg-yellow-900/50 text-yellow-400 rounded-full text-sm animate-pulse">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Reconnecting... ({reconnectAttempts}/5)
                </div>
              )}
              
              {/* Signaling status */}
              {isCallActive && (
                <div className={`flex items-center gap-2 px-2 py-1 rounded-full text-xs ${
                  signalingConnected ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${signalingConnected ? 'bg-green-500' : 'bg-red-500'}`} />
                  {signalingConnected ? 'Signal' : 'No Signal'}
                </div>
              )}
              
              {isCallActive && (
                <>
                  {isRecording && (
                    <span className="flex items-center gap-2 px-3 py-1 bg-red-900/50 text-red-400 rounded-full text-sm">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      Recording
                    </span>
                  )}
                  <span className="flex items-center gap-2 px-3 py-1 bg-green-900/50 text-green-400 rounded-full text-sm">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    {formatDuration(callDuration)}
                  </span>
                </>
              )}
              
              <button 
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Settings className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>

          {/* Video Container */}
          <div className="flex-1 relative">
            {isCallActive ? (
              <>
                {/* Remote Video */}
                <div className={`absolute inset-0 ${showNotes || showChat ? 'right-80' : ''} bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center transition-all`}>
                  {remoteStream ? (
                    <video 
                      ref={remoteVideoRef}
                      autoPlay 
                      playsInline
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="text-center">
                      <div className="w-40 h-40 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl">
                        <span className="text-5xl text-white font-bold">{selectedClient?.name.split(' ').map(n => n[0]).join('')}</span>
                      </div>
                      <h2 className="text-white text-3xl font-bold">{selectedClient?.name}</h2>
                      <p className="text-gray-400 text-lg mt-1">{selectedClient?.type}</p>
                      <p className="text-green-400 mt-3 flex items-center justify-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        Connected
                      </p>
                    </div>
                  )}
                </div>

                {/* Local Video (Picture in Picture) */}
                <div className="absolute bottom-24 right-6 w-56 h-40 bg-gray-700 rounded-xl shadow-2xl overflow-hidden border-2 border-gray-600">
                  {isVideoOn && localStream ? (
                    <video 
                      ref={localVideoRef}
                      autoPlay 
                      playsInline
                      muted
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                      <VideoOff className="w-10 h-10 text-gray-500" />
                    </div>
                  )}
                  <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 rounded text-white text-xs">
                    You {isMuted && '(Muted)'}
                  </div>
                </div>
              </>
            ) : (
              /* Pre-session / Waiting Room */
              <div className="absolute inset-0 flex">
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center max-w-md">
                    <div className="w-24 h-24 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Video className="w-12 h-12 text-gray-500" />
                    </div>
                    <h2 className="text-white text-2xl font-bold mb-2">Ready to Start Session</h2>
                    <p className="text-gray-400 mb-6">Select a client from the waiting room to begin</p>
                    
                    <div className="bg-gray-800 rounded-xl p-4 text-left">
                      <h4 className="text-white font-medium mb-3">Quick Device Check</h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400 flex items-center gap-2">
                            <Camera className="w-4 h-4" /> Camera
                          </span>
                          <span className="text-green-400 flex items-center gap-1">
                            <Check className="w-4 h-4" /> Ready
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400 flex items-center gap-2">
                            <Mic className="w-4 h-4" /> Microphone
                          </span>
                          <span className="text-green-400 flex items-center gap-1">
                            <Check className="w-4 h-4" /> Ready
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400 flex items-center gap-2">
                            <Wifi className="w-4 h-4" /> Connection
                          </span>
                          <span className="text-green-400 flex items-center gap-1">
                            <Check className="w-4 h-4" /> Excellent
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Waiting Room */}
                {waitingRoomClients.length > 0 && (
                  <div className="w-80 bg-gray-800 border-l border-gray-700 p-4">
                    <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-orange-500" />
                      Waiting Room ({waitingRoomClients.length})
                    </h3>
                    <div className="space-y-3">
                      {waitingRoomClients.map((client) => (
                        <div key={client.id} className="bg-gray-700 rounded-xl p-4">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                              {client.name.split(' ').map(n => n[0]).join('')}
                            </div>
                            <div className="flex-1">
                              <h4 className="text-white font-medium">{client.name}</h4>
                              <p className="text-xs text-gray-400">Waiting since {client.waitingSince}</p>
                            </div>
                          </div>
                          <p className="text-sm text-gray-400 mb-3">{client.type}</p>
                          <button 
                            onClick={() => startSession(client)}
                            className="w-full py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                          >
                            <Shield className="w-4 h-4" />
                            Admit (HIPAA)
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Call Controls */}
            {isCallActive && (
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex items-center gap-2 bg-gray-800/95 backdrop-blur-sm px-4 py-3 rounded-2xl shadow-2xl border border-gray-700">
                <button 
                  onClick={toggleMute}
                  className={`p-3 rounded-xl transition-all ${isMuted ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                  title={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted ? <MicOff className="w-5 h-5 text-white" /> : <Mic className="w-5 h-5 text-white" />}
                </button>
                
                <button 
                  onClick={toggleVideo}
                  className={`p-3 rounded-xl transition-all ${!isVideoOn ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                  title={isVideoOn ? 'Turn off camera' : 'Turn on camera'}
                >
                  {isVideoOn ? <Video className="w-5 h-5 text-white" /> : <VideoOff className="w-5 h-5 text-white" />}
                </button>
                
                <button 
                  onClick={toggleScreenShare}
                  className={`p-3 rounded-xl transition-all relative ${isScreenSharing ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                  title="Share screen (HIPAA)"
                >
                  <Monitor className="w-5 h-5 text-white" />
                  <Shield className="w-3 h-3 text-yellow-400 absolute -top-1 -right-1" />
                </button>

                <button 
                  onClick={openWhiteboard}
                  className={`p-3 rounded-xl transition-all relative ${showWhiteboard ? 'bg-purple-500 hover:bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                  title="Whiteboard (HIPAA)"
                >
                  <PenTool className="w-5 h-5 text-white" />
                  <Shield className="w-3 h-3 text-yellow-400 absolute -top-1 -right-1" />
                </button>

                <div className="w-px h-8 bg-gray-600" />

                <button 
                  onClick={() => setViewMode(viewMode === 'speaker' ? 'gallery' : 'speaker')}
                  className="p-3 rounded-xl bg-gray-700 hover:bg-gray-600 transition-all"
                  title="Change view"
                >
                  {viewMode === 'speaker' ? <Grid className="w-5 h-5 text-white" /> : <Layout className="w-5 h-5 text-white" />}
                </button>

                <button 
                  onClick={() => { setShowChat(!showChat); setShowNotes(false); }}
                  className={`p-3 rounded-xl transition-all ${showChat ? 'bg-orange-500 hover:bg-orange-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                  title="Chat"
                >
                  <MessageSquare className="w-5 h-5 text-white" />
                </button>

                <button 
                  onClick={() => { setShowNotes(!showNotes); setShowChat(false); }}
                  className={`p-3 rounded-xl transition-all ${showNotes ? 'bg-orange-500 hover:bg-orange-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                  title="Session notes"
                >
                  <FileText className="w-5 h-5 text-white" />
                </button>

                <button 
                  onClick={toggleRecording}
                  className={`p-3 rounded-xl transition-all relative ${isRecording ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                  title={isRecording ? 'Stop recording (HIPAA)' : 'Start recording (HIPAA)'}
                >
                  {isRecording ? <Pause className="w-5 h-5 text-white" /> : <div className="w-5 h-5 bg-red-500 rounded-full" />}
                  <Shield className="w-3 h-3 text-yellow-400 absolute -top-1 -right-1" />
                </button>

                <div className="w-px h-8 bg-gray-600" />
                
                <button 
                  onClick={endSession}
                  className="p-3 bg-red-500 rounded-xl hover:bg-red-600 transition-all"
                  title="End session"
                >
                  <PhoneOff className="w-5 h-5 text-white" />
                </button>
              </div>
            )}
          </div>

          {/* Side Panel */}
          {isCallActive && (showChat || showNotes) && (
            <div className="absolute top-[57px] right-0 bottom-0 w-80 bg-gray-800 border-l border-gray-700 flex flex-col">
              {showChat && (
                <>
                  <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                    <h3 className="text-white font-bold">Session Chat</h3>
                    <button onClick={() => setShowChat(false)} className="text-gray-400 hover:text-white">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex-1 p-4 overflow-y-auto space-y-3">
                    {messages.length === 0 && (
                      <p className="text-gray-500 text-sm text-center mt-8">No messages yet</p>
                    )}
                    {messages.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.sender === 'therapist' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                          msg.sender === 'therapist' ? 'bg-orange-500 text-white rounded-br-md' : 'bg-gray-700 text-gray-200 rounded-bl-md'
                        }`}>
                          <p className="text-sm">{msg.text}</p>
                          <p className="text-xs opacity-70 mt-1">{msg.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-4 border-t border-gray-700">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        placeholder="Type a message..."
                        className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                      <button 
                        onClick={sendMessage}
                        className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                      >
                        Send
                      </button>
                    </div>
                  </div>
                </>
              )}
              
              {showNotes && (
                <>
                  <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                    <h3 className="text-white font-bold">Session Notes</h3>
                    <button onClick={() => setShowNotes(false)} className="text-gray-400 hover:text-white">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex-1 p-4 flex flex-col">
                    <div className="mb-3 text-sm">
                      <p className="text-gray-400">Client: <span className="text-white">{selectedClient?.name}</span></p>
                      <p className="text-gray-400">Session: <span className="text-white">{selectedClient?.type}</span></p>
                      <p className="text-gray-400">Duration: <span className="text-white">{formatDuration(callDuration)}</span></p>
                    </div>
                    <textarea
                      value={sessionNotes}
                      onChange={(e) => setSessionNotes(e.target.value)}
                      placeholder="Take session notes here..."
                      className="flex-1 bg-gray-700 text-white p-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                    />
                    <div className="mt-3 flex gap-2">
                      <button className="flex-1 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 text-sm">
                        Use Template
                      </button>
                      <button 
                        onClick={saveNotes}
                        className="flex-1 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm"
                      >
                        Save Notes
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        {!isCallActive && (
          <div className="w-80 bg-gray-800 border-l border-gray-700 flex flex-col">
            <div className="p-4 border-b border-gray-700">
              <h3 className="text-white font-bold mb-2">Today's Sessions</h3>
              <p className="text-gray-400 text-sm">{clients.length} appointments scheduled</p>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {clients.map((client) => (
                <div 
                  key={client.id}
                  className="p-4 rounded-xl bg-gray-700 hover:bg-gray-600 cursor-pointer transition-all"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium">{client.name}</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      client.status === 'ready' ? 'bg-green-500/20 text-green-400' :
                      client.status === 'waiting' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-gray-600 text-gray-400'
                    }`}>
                      {client.status}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm">{client.time} - {client.type}</p>
                  {(client.status === 'ready' || client.status === 'waiting') && (
                    <button 
                      onClick={() => startSession(client)}
                      className="mt-3 w-full py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center justify-center gap-2 text-sm font-medium"
                    >
                      <Shield className="w-4 h-4" />
                      Start (HIPAA)
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {showSettings && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-xl w-full max-w-md shadow-2xl">
              <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-white font-bold">Session Settings</h3>
                <button onClick={() => setShowSettings(false)} className="text-gray-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <label className="text-white font-medium block mb-2">Camera</label>
                  <select className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg">
                    <option>Default Camera</option>
                  </select>
                </div>
                <div>
                  <label className="text-white font-medium block mb-2">Microphone</label>
                  <select className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg">
                    <option>Default Microphone</option>
                  </select>
                </div>
                <div>
                  <label className="text-white font-medium block mb-2">Speaker</label>
                  <select className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg">
                    <option>Default Speaker</option>
                  </select>
                </div>
              </div>
              <div className="p-4 border-t border-gray-700 flex justify-end">
                <button 
                  onClick={() => setShowSettings(false)}
                  className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BehavioralHealthTelehealth;
